/**
 * Alap domain vocabulary.
 *
 * This file is the contract between the API, the mobile app and the admin
 * console. Everything here maps to a decision in the design document; the
 * section reference is on the comment so the two stay honest with each other.
 */

/* ------------------------------------------------------------------ *
 * Relationship structure (design doc §05)
 * ------------------------------------------------------------------ */

export const STRUCTURES = [
  'solo',
  'partnered_separately',
  'partnered_together',
  'partnered_one_of_us',
  'comet',
  'figuring_it_out',
] as const;
export type Structure = (typeof STRUCTURES)[number];

export interface StructureMeta {
  value: Structure;
  label: string;
  labelBn: string;
  blurb: string;
  /** Does this structure describe someone who has a partner? */
  partnered: boolean;
  /** Can the profile publish without an active partner co-sign? */
  requiresCosign: boolean;
  /** Is this a joint profile operated by two linked accounts? */
  isCouple: boolean;
}

export const STRUCTURE_META: Record<Structure, StructureMeta> = {
  solo: {
    value: 'solo',
    label: 'Solo — practising ENM',
    labelBn: 'একা — খোলা সম্পর্কে',
    blurb: 'Unpartnered, and dating on my own terms.',
    partnered: false,
    requiresCosign: false,
    isCouple: false,
  },
  partnered_separately: {
    value: 'partnered_separately',
    label: 'Partnered — we each date separately',
    labelBn: 'সঙ্গী আছে — আমরা আলাদা ভাবে ডেট করি',
    blurb: 'I have a partner. We see other people independently.',
    partnered: true,
    requiresCosign: false,
    isCouple: false,
  },
  partnered_together: {
    value: 'partnered_together',
    label: 'Partnered — we date together',
    labelBn: 'সঙ্গী আছে — আমরা একসাথে ডেট করি',
    blurb: 'A couple profile. Both of us are here, both of us write.',
    partnered: true,
    requiresCosign: true,
    isCouple: true,
  },
  partnered_one_of_us: {
    value: 'partnered_one_of_us',
    label: "Partnered — only I'm dating",
    labelBn: 'সঙ্গী আছে — শুধু আমি ডেট করছি',
    blurb: 'My partner knows and is not dating anyone themselves.',
    partnered: true,
    requiresCosign: false,
    isCouple: false,
  },
  comet: {
    value: 'comet',
    label: 'Comet — long-distance by design',
    labelBn: 'ধূমকেতু — দূরত্ব নিয়েই',
    blurb: 'Episodic and long-distance. I am upfront about the gaps.',
    partnered: false,
    requiresCosign: false,
    isCouple: false,
  },
  figuring_it_out: {
    value: 'figuring_it_out',
    label: 'Still figuring it out',
    labelBn: 'এখনও ভাবছি',
    blurb: 'Curious, newly open, not yet settled on a shape.',
    partnered: false,
    requiresCosign: false,
    isCouple: false,
  },
};

/** Structures that trigger the non-skippable awareness attestation (§05). */
export function requiresAwarenessAttestation(s: Structure): boolean {
  return STRUCTURE_META[s].partnered;
}

/* ------------------------------------------------------------------ *
 * Verification tiers (design doc §08)
 * Deliberately never purchasable — a safety signal that can be bought
 * is not a safety signal.
 * ------------------------------------------------------------------ */

export const VERIFICATIONS = ['live', 'phone', 'age_proofed', 'cosigned', 'vouched'] as const;
export type Verification = (typeof VERIFICATIONS)[number];

export const MANDATORY_VERIFICATIONS: Verification[] = ['live', 'phone'];

/* ------------------------------------------------------------------ *
 * Consent ledger (design doc §05, §11)
 * Append-only. Doubles as the DPDP Act consent record.
 * ------------------------------------------------------------------ */

export const CONSENT_KINDS = [
  'data_processing',
  'contact_matching',
  'photo_unlock',
  'partner_link',
  'precise_location',
  'media_receive',
  'marketing',
  /**
   * Interpersonal consent, recorded in the same append-only ledger as data
   * consent (§05, §09) — deliberately, because the two need identical
   * properties: revocable at any moment, without a reason, taking effect
   * immediately, and auditable afterwards. A second mechanism would drift.
   */
  'topic_gate',
  'boundary_share',
] as const;
export type ConsentKind = (typeof CONSENT_KINDS)[number];

export const CONSENT_LABELS: Record<ConsentKind, string> = {
  data_processing: 'Storing and processing my profile',
  contact_matching: 'Matching salted hashes of my contacts so we stay hidden from each other',
  photo_unlock: 'Showing my photos to this person',
  partner_link: 'Linking my profile to my partner',
  precise_location: 'Using precise location instead of an approximate area',
  media_receive: 'Receiving photos from this person',
  marketing: 'Email and push about events and updates',
  topic_gate:
    'Opening a topic in one conversation. Either of you can close it again at any time, with no reason given and nothing said to the other person about why.',
  boundary_share: 'Showing my boundary card to someone I am talking to',
};

/* ------------------------------------------------------------------ *
 * Gender, pronouns, orientation — open sets, not a binary (§06)
 * ------------------------------------------------------------------ */

export const GENDERS = [
  'woman',
  'man',
  'non_binary',
  'trans_woman',
  'trans_man',
  'genderfluid',
  'agender',
  'self_describe',
] as const;
export type Gender = (typeof GENDERS)[number];

/** Cohorts used for the ratio guardrail and the launch pricing rule (§12, §13). */
export type RatioCohort = 'men' | 'women_nb';

export function ratioCohort(g: Gender): RatioCohort {
  return g === 'man' || g === 'trans_man' ? 'men' : 'women_nb';
}

export const ORIENTATIONS = [
  'straight',
  'gay',
  'lesbian',
  'bisexual',
  'pansexual',
  'queer',
  'asexual',
  'demisexual',
  'questioning',
] as const;
export type Orientation = (typeof ORIENTATIONS)[number];

/* ------------------------------------------------------------------ *
 * Privacy: circle blocks (design doc §07, §11)
 * ------------------------------------------------------------------ */

export const CIRCLE_KINDS = ['employer', 'college', 'neighbourhood'] as const;
export type CircleKind = (typeof CIRCLE_KINDS)[number];

/** Warn (do not hide) once two people share this many contact hashes (§07). */
export const SMALL_WORLD_WARN_THRESHOLD = 3;

/* ------------------------------------------------------------------ *
 * Trust & safety (design doc §08)
 * ------------------------------------------------------------------ */

export const REPORT_REASONS = [
  'ncii',
  'suspected_minor',
  'coercion',
  'solicitation',
  'catfish',
  'undisclosed_monogamy',
  'harassment',
  'spam',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export interface ReportPolicy {
  reason: ReportReason;
  label: string;
  /** First-response target, in minutes. Drives moderation queue ordering. */
  firstResponseMins: number;
  /** Hide the reported profile immediately, before a human looks? */
  autoHide: boolean;
  /** Preserve evidence rather than deleting on ban (legal hold). */
  preserveEvidence: boolean;
}

export const REPORT_POLICY: Record<ReportReason, ReportPolicy> = {
  suspected_minor: {
    reason: 'suspected_minor',
    label: 'I think this person is under 18',
    firstResponseMins: 60,
    autoHide: true,
    preserveEvidence: true,
  },
  ncii: {
    reason: 'ncii',
    label: 'Intimate images shared without consent',
    firstResponseMins: 120,
    autoHide: true,
    preserveEvidence: true,
  },
  coercion: {
    reason: 'coercion',
    label: 'Threats, coercion or stalking',
    firstResponseMins: 240,
    autoHide: true,
    preserveEvidence: true,
  },
  solicitation: {
    reason: 'solicitation',
    label: 'Selling or soliciting paid services',
    firstResponseMins: 720,
    autoHide: false,
    preserveEvidence: false,
  },
  catfish: {
    reason: 'catfish',
    label: 'Not who they say they are',
    firstResponseMins: 720,
    autoHide: false,
    preserveEvidence: false,
  },
  undisclosed_monogamy: {
    reason: 'undisclosed_monogamy',
    label: "Their partner doesn't know they're here",
    firstResponseMins: 2880,
    autoHide: false,
    preserveEvidence: false,
  },
  harassment: {
    reason: 'harassment',
    label: 'Abuse or harassment',
    firstResponseMins: 240,
    autoHide: false,
    preserveEvidence: true,
  },
  spam: {
    reason: 'spam',
    label: 'Spam or a bot',
    firstResponseMins: 1440,
    autoHide: false,
    preserveEvidence: false,
  },
  other: {
    reason: 'other',
    label: 'Something else',
    firstResponseMins: 1440,
    autoHide: false,
    preserveEvidence: false,
  },
};

/* ------------------------------------------------------------------ *
 * Classifier-raised case reasons (§08)
 *
 * Deliberately NOT part of REPORT_REASONS. That list is the taxonomy a member
 * picks from in the report screen, and apps/mobile/app/safety/report.tsx builds
 * its picker by iterating it — so anything added there becomes a menu option
 * people choose. These reasons are raised by the machine and never by a person,
 * so they need a queue label and an SLA without appearing in that menu.
 *
 * They share ReportPolicy's shape, and policyFor() reads both maps, so a
 * classifier case is ordered in the queue by the same clock as a reported one.
 * ------------------------------------------------------------------ */

export const CLASSIFIER_REASONS = ['consent_drift'] as const;
export type ClassifierReason = (typeof CLASSIFIER_REASONS)[number];

export const CLASSIFIER_POLICY: Record<ClassifierReason, ReportPolicy> = {
  consent_drift: {
    // The cast is the point: this reason is a case reason, not a report reason.
    reason: 'consent_drift' as unknown as ReportReason,
    /**
     * Names the pattern, not a verdict. A moderator opening this is reading a
     * conversation where someone said no and the other person kept going — and
     * "kept going" is sometimes a misread, which is why a human is here at all.
     */
    label: 'Someone kept pushing after a no',
    // The harassment tier: four hours, not a day (§08).
    firstResponseMins: 240,
    // Never. Nothing is hidden before a human has read the thread — a
    // false positive here would punish someone for an ordinary conversation.
    autoHide: false,
    preserveEvidence: true,
  },
};

/** Every reason a case can carry, however it was raised. */
export type CaseReason = ReportReason | ClassifierReason;

export const CASE_POLICY: Record<string, ReportPolicy> = {
  ...REPORT_POLICY,
  ...CLASSIFIER_POLICY,
};

export const MODERATION_VERDICTS = [
  'no_action',
  'warned',
  'content_removed',
  'suspended',
  'banned',
] as const;
export type ModerationVerdict = (typeof MODERATION_VERDICTS)[number];

/**
 * Content Safety categories. Mirrors the Azure AI Content Safety taxonomy plus
 * the India-specific categories generic models miss (§08, §10).
 */
export const SAFETY_CATEGORIES = [
  'sexual',
  'violence',
  'hate',
  'self_harm',
  'solicitation',
  'contact_exfiltration',
  'minor_signal',
] as const;
export type SafetyCategory = (typeof SAFETY_CATEGORIES)[number];

export interface SafetyVerdict {
  allowed: boolean;
  /** 0-6, Azure Content Safety severity convention. */
  scores: Partial<Record<SafetyCategory, number>>;
  /** Categories that breached their block threshold. */
  blockedBy: SafetyCategory[];
  /** Human-readable reason shown to the user when we refuse content. */
  message?: string;
  /** True when produced by the offline heuristic rather than Azure. */
  mock: boolean;
}

/** Zero nudity anywhere, including private chat (§08). Non-negotiable. */
export const SAFETY_BLOCK_THRESHOLDS: Record<SafetyCategory, number> = {
  sexual: 2,
  violence: 4,
  hate: 2,
  self_harm: 4,
  solicitation: 3,
  contact_exfiltration: 4,
  minor_signal: 1,
};

/* ------------------------------------------------------------------ *
 * Discovery & quotas (design doc §04, §12)
 * ------------------------------------------------------------------ */

export const DEFAULT_FREE_DAILY_REPLIES = 8;
export const PLUS_DAILY_REPLIES = 1000;

export const PLANS = ['free', 'pass_7d', 'plus_monthly', 'plus_quarterly', 'couple'] as const;
export type Plan = (typeof PLANS)[number];

export interface PlanMeta {
  id: Plan;
  label: string;
  /** Paise, inclusive of 18% GST — see §12 on why the price is GST-inclusive. */
  amountPaise: number;
  durationDays: number;
  dailyReplies: number;
}

/* ------------------------------------------------------------------ *
 * Likes and reveals (§04, §12 — added post-design by product decision)
 *
 * The design doc argued there should be no like button, because a like costs
 * nothing and the resulting volume is what makes these apps miserable for the
 * people receiving it. The decision to ship one anyway makes these three
 * numbers the entire mitigation:
 *
 *   - the SEND cap keeps volume finite (10/day, against 8 free replies);
 *   - the REVEAL ladder means the recipient is never forced to pay to know
 *     that they are wanted — the first one every day is always free;
 *   - the ad rung exists so the second reveal has a non-monetary price.
 *
 * If any of these is ever raised, the ratio guardrail in §13 is the thing that
 * will show it first.
 * ------------------------------------------------------------------ */

/** Likes a member may SEND per IST day, on any plan. */
export const DAILY_LIKE_LIMIT = 10;

/** Reveals per IST day at each rung, before the next rung is required. */
export const FREE_REVEALS_PER_DAY = 1;
export const AD_REVEALS_PER_DAY = 1;

/** How a reveal was paid for. Order is the ladder. */
export const REVEAL_METHODS = ['free', 'ad', 'subscription'] as const;
export type RevealMethod = (typeof REVEAL_METHODS)[number];

export const PLAN_META: Record<Plan, PlanMeta> = {
  free: { id: 'free', label: 'Free', amountPaise: 0, durationDays: 0, dailyReplies: DEFAULT_FREE_DAILY_REPLIES },
  pass_7d: { id: 'pass_7d', label: '7-day pass', amountPaise: 9900, durationDays: 7, dailyReplies: PLUS_DAILY_REPLIES },
  plus_monthly: { id: 'plus_monthly', label: 'Alap+ monthly', amountPaise: 29900, durationDays: 30, dailyReplies: PLUS_DAILY_REPLIES },
  plus_quarterly: { id: 'plus_quarterly', label: 'Alap+ quarterly', amountPaise: 69900, durationDays: 90, dailyReplies: PLUS_DAILY_REPLIES },
  couple: { id: 'couple', label: 'Couple plan', amountPaise: 44900, durationDays: 30, dailyReplies: PLUS_DAILY_REPLIES },
};

/* ------------------------------------------------------------------ *
 * Threads & media (design doc §06)
 * ------------------------------------------------------------------ */

export const MEDIA_GATE_STATES = ['none', 'requested', 'once', 'always', 'refused'] as const;
export type MediaGateState = (typeof MEDIA_GATE_STATES)[number];

/* ------------------------------------------------------------------ *
 * Topic gates (§05, §09)
 *
 * media_gate generalised. A conversation opens at 'ordinary' and stays there
 * until BOTH people move it: one asks, the other accepts, and either can close
 * it again at any moment with no reason given and no notification sent about
 * why. That last property is the whole design — a gate you must justify
 * closing is not a gate.
 *
 * Nothing about this unlocks images. Photos keep their own separate gate, and
 * explicit imagery is refused at every threshold regardless of any state here.
 * ------------------------------------------------------------------ */

export const TOPIC_GATE_STATES = ['ordinary', 'requested', 'open', 'declined'] as const;
export type TopicGateState = (typeof TOPIC_GATE_STATES)[number];

/**
 * What a gate can be opened FOR. Deliberately a short, named list rather than
 * free text: a gate whose subject is unbounded cannot be reasoned about by
 * moderation, and "we agreed to anything" is not informed consent.
 */
export const GATE_TOPICS = ['candour', 'photos_beyond_profile', 'meeting_logistics'] as const;
export type GateTopic = (typeof GATE_TOPICS)[number];

export const GATE_TOPIC_LABELS: Record<GateTopic, string> = {
  candour: 'Talking frankly about what we each want',
  photos_beyond_profile: 'Sharing photos beyond our profiles',
  meeting_logistics: 'Working out where and when to meet',
};

/* ------------------------------------------------------------------ *
 * Boundary cards (§05)
 *
 * Optional, structured, revocable. Written once and shown before a first
 * meeting rather than discovered during one. Kept as a small fixed vocabulary
 * plus one free-text line, because a wall of prose is not read and a checkbox
 * grid is not honest.
 * ------------------------------------------------------------------ */

export const BOUNDARY_KINDS = ['looking_for', 'not_looking_for', 'ask_first', 'hard_limit'] as const;
export type BoundaryKind = (typeof BOUNDARY_KINDS)[number];

export const BOUNDARY_LABELS: Record<BoundaryKind, string> = {
  looking_for: 'What I am looking for',
  not_looking_for: 'What I am not looking for',
  ask_first: 'Ask me first about',
  hard_limit: 'Not up for discussion',
};

/** A hard limit is the one kind that is never softened by any gate state. */
export const BOUNDARY_MAX_CHARS = 240;
export const MAX_BOUNDARIES_PER_KIND = 4;

/* ------------------------------------------------------------------ *
 * Line reactions (§04)
 *
 * A reaction attaches to a SENTENCE of someone's answer, never to the person.
 * "This line" is a compliment with evidence; a like on a human being is the
 * thing §04 spent two pages arguing against.
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * Aftercare (§08)
 *
 * The quiet half of the check-in, asked about a day after a meeting. Four
 * coarse options and nothing finer, because a five-star score on a human being
 * turns a safety mechanism into a reputation system — and reputation systems
 * in dating apps punish the people who report. 'no_answer' is a real answer,
 * recorded when someone dismisses the prompt, so it is never asked twice.
 * ------------------------------------------------------------------ */

export const AFTERCARE_SENTIMENTS = ['good', 'mixed', 'bad', 'no_answer'] as const;
export type AftercareSentiment = (typeof AFTERCARE_SENTIMENTS)[number];

export const AFTERCARE_LABELS: Record<AftercareSentiment, string> = {
  good: 'Good — I would see them again',
  mixed: 'Mixed',
  bad: 'Not good',
  no_answer: 'Rather not say',
};

/** Optional and short. A form that wants an essay gets nothing. */
export const AFTERCARE_NOTE_MAX = 500;

export const LINE_REACTIONS = ['this_line', 'made_me_laugh', 'same', 'tell_me_more'] as const;
export type LineReaction = (typeof LINE_REACTIONS)[number];

export const LINE_REACTION_LABELS: Record<LineReaction, string> = {
  this_line: 'This line',
  made_me_laugh: 'Made me laugh',
  same: 'Same',
  tell_me_more: 'Tell me more',
};

export const MESSAGE_KINDS = ['text', 'voice', 'photo', 'date_plan', 'system'] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

/* ------------------------------------------------------------------ *
 * Geography — West Bengal launch clusters (design doc §14)
 * Order matters: it is the launch sequence.
 * ------------------------------------------------------------------ */

export interface Cluster {
  id: string;
  name: string;
  city: string;
  phase: 0 | 1 | 2 | 3;
  lat: number;
  lon: number;
}

export const CLUSTERS: Cluster[] = [
  { id: 'south_kolkata', name: 'South Kolkata', city: 'Kolkata', phase: 0, lat: 22.5186, lon: 88.3639 },
  { id: 'salt_lake', name: 'Salt Lake & Sector V', city: 'Kolkata', phase: 1, lat: 22.5697, lon: 88.4324 },
  { id: 'new_town', name: 'New Town', city: 'Kolkata', phase: 1, lat: 22.5958, lon: 88.4790 },
  { id: 'jadavpur', name: 'Jadavpur', city: 'Kolkata', phase: 1, lat: 22.4991, lon: 88.3714 },
  { id: 'behala', name: 'Behala', city: 'Kolkata', phase: 2, lat: 22.4986, lon: 88.3096 },
  { id: 'north_kolkata', name: 'North Kolkata', city: 'Kolkata', phase: 2, lat: 22.5958, lon: 88.3697 },
  { id: 'howrah', name: 'Howrah', city: 'Howrah', phase: 3, lat: 22.5958, lon: 88.2636 },
  { id: 'siliguri', name: 'Siliguri', city: 'Siliguri', phase: 3, lat: 26.7271, lon: 88.3953 },
  { id: 'durgapur', name: 'Durgapur–Asansol', city: 'Durgapur', phase: 3, lat: 23.5204, lon: 87.3119 },
];

/**
 * Order is the shipping order and the display order, and they are the same on
 * purpose: English is the base every member falls back to, Hindi is what the
 * national phase runs on (§15 P4), Bengali is the launch region. Every picker
 * in the app renders straight from this array, so changing the priority is
 * this line and nothing else.
 */
export const SUPPORTED_LOCALES = ['en', 'hi', 'bn'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/* ------------------------------------------------------------------ *
 * Ratio guardrail (design doc §13)
 * Wired to an automatic switch, deliberately, so nobody has to call a
 * meeting to decide whether to intervene.
 * ------------------------------------------------------------------ */

export const RATIO_PAUSE_THRESHOLD = 0.70;

export function shouldPauseMaleIntake(menCount: number, womenNbCount: number, threshold = RATIO_PAUSE_THRESHOLD): boolean {
  const total = menCount + womenNbCount;
  if (total < 50) return false; // too small to be a signal
  return menCount / total > threshold;
}

/* ------------------------------------------------------------------ *
 * Phone-OTP resend backoff (design doc §06)
 *
 * The wait before the NEXT code climbs with every code already sent:
 * one minute, then five, then ten, then an hour — and an hour for every
 * request after that. Two reasons it is a ladder and not a flat cap:
 *
 *   1. A real person who mistypes their number, or whose carrier holds
 *      the first SMS, gets a second code in a minute. A flat "three an
 *      hour" punishes them for the carrier's mistake.
 *   2. Someone farming SMS at our expense hits an hour on their fourth
 *      request, which makes the attack cost more than it returns.
 *
 * The ladder IS the rate limit — there is no separate hourly cap to
 * disagree with it. Climbing it takes 76 minutes, so a number cannot
 * pull more than four codes in a rolling hour no matter what it does.
 * An hour of quiet drops you back to the first rung.
 * ------------------------------------------------------------------ */

export const OTP_RESEND_LADDER_SEC = [60, 300, 600, 3600] as const;

/** The ladder's top rung, which is also how long a burst takes to forget. */
export const OTP_BURST_WINDOW_SEC = 3600;

/**
 * How long to wait after `sent` codes before another may be requested.
 * `sent` is the count already delivered in this burst, so sent=1 is the
 * wait before the second code. Past the last rung it stays at the top.
 */
export function otpResendWaitSec(sent: number): number {
  if (sent < 1) return 0;
  const i = Math.min(sent, OTP_RESEND_LADDER_SEC.length) - 1;
  return OTP_RESEND_LADDER_SEC[i] ?? OTP_BURST_WINDOW_SEC;
}

/** "1 minute" / "5 minutes" / "an hour" — for copy, never for logic. */
export function humanWait(seconds: number): string {
  if (seconds >= 3600) {
    const h = Math.round(seconds / 3600);
    return h === 1 ? 'an hour' : `${h} hours`;
  }
  if (seconds >= 60) {
    const m = Math.ceil(seconds / 60);
    return `${m} minute${m === 1 ? '' : 's'}`;
  }
  return `${Math.max(1, seconds)} second${seconds === 1 ? '' : 's'}`;
}
